import { useMatch, useNavigate, useParams } from "react-router-dom";
import { Card, Grid, Stack, TextField, Button, Box, Typography, Chip, Paper } from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import { roleFormValidationSchema } from "src/util/validationSchemas";
import { getRoleUrlId, roleUrl } from "src/react-query/endPoints";
import { useList, useGetDetail, useMutate } from "src/hooks/useFetch.hook";
import { useEffect } from "react";
import { BaseTable } from "../BaseTable/BaseTable";
import { getRoleDefaultValue, featureColumns } from "./roles.constants";
import { LoadingButton } from "@mui/lab";
import { Link as RouterLink } from "react-router-dom";
import { useAccess } from "src/hooks/useAccess.hook";
import { Icon } from "@iconify/react";
import { FEATURE } from "src/util/enums";
import { is } from "src/util/is";
import { Loader } from "../Loader";
import { commonSlice } from "src/store/common/common.slice";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import lockFill from "@iconify/icons-eva/lock-fill";
import settingsFill from "@iconify/icons-eva/settings-fill";
import checkmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import closeCircleFill from "@iconify/icons-eva/close-circle-fill";

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));


const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  border: `1px solid ${theme.palette.primary.light}40`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  flex: 1,
  marginBottom: theme.spacing(3),
}));

const PermissionsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
}));

const ActionSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
  background: theme.palette.grey[50],
}));

export const RoleForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const featuresAccess = useSelector(({ common }) => common.features);
  const { mutate, isLoading } = useMutate("roles");
  const isCreateRole = useMatch("/roles/new");
  const { roleId } = useParams();
  const hasReadOnlyAccess = useAccess(FEATURE.ROLES, "R");
  const isReadOnly = hasReadOnlyAccess && !isCreateRole;
  const { data: role, isFetching } = useGetDetail("role", {
    id: roleId,
  });
  const { data: features, isFetching: featureLoading } = useList(
    "features",
    is.empty(roleId)
  );
  const onSubmit = async (roleData, { setSubmitting }) => {
    const mutateObject = {
      url: roleUrl,
      method: "POST",
    };

    if (!is.nullOrUndefined(roleId) && !isCreateRole) {
      mutateObject.method = "PUT";
      mutateObject.url = getRoleUrlId(roleId);
    }

    mutate(
      {
        url: mutateObject.url,
        method: mutateObject.method,
        body: { ...roleData, access: featuresAccess },
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const onSuccess = () => navigate("/roles");

  const formik = useFormik({
    initialValues: getRoleDefaultValue(),
    validationSchema: roleFormValidationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
  } = formik;

  useEffect(() => {
    if (role) {
      resetForm({ values: role });
      const access = [...(role?.access ?? [])];

      // Add ChargingSite feature if not available from backend
      const existingChargingSite = access.find(feature => feature.featureName === "ChargingSite");
      if (!existingChargingSite) {
        const chargingSiteFeature = {
          featureId: 22,
          featureName: "ChargingSite",
          create: "N",
          view: "N",
          update: "N",
          delete: "N",
        };
        access.push(chargingSiteFeature);
      }

      // Add ev driver feature if not available from backend
      const existingEvDriver = access.find(feature => feature.featureName === "EvDriver");
      if (!existingEvDriver) {
        const evDriverFeature = {
          featureId: 26,
          featureName: "EvDriver",
          create: "N",
          view: "N",
          update: "N",
          delete: "N",
        };
        access.push(evDriverFeature);
      }

      dispatch(commonSlice.actions.setFeatures(access));
    }
  }, [role, resetForm, dispatch]);

  useEffect(() => {
    if (!is.empty(features)) {
      const access =
        features?.map((feature) => ({
          ...feature,
          create: "N",
          view: "N",
          update: "N",
          delete: "N",
        })) ?? [];

      // Add ChargingSite feature if not available from backend
      const existingChargingSite = access.find(feature => feature.featureName === "ChargingSite");
      if (!existingChargingSite) {
        const chargingSiteFeature = {
          featureId: 22,
          featureName: "ChargingSite",
          create: "N",
          view: "N",
          update: "N",
          delete: "N",
        };
        access.push(chargingSiteFeature);
      }

      // Add ev driver feature if not available from backend
      const existingEvDriver = access.find(feature => feature.featureName === "EvDriver");
      if (!existingEvDriver) {
        const evDriverFeature = {
          featureId: 26,
          featureName: "EvDriver",
          create: "N",
          view: "N",
          update: "N",
          delete: "N",
        };
        access.push(evDriverFeature);
      }

      is.empty(roleId) && dispatch(commonSlice.actions.setFeatures(access));
    }
  }, [dispatch, features, roleId]);

  if (isFetching) {
    return <Loader />;
  }

  if (!isCreateRole && is.empty(role)) {
    navigate("/roles");
    return null;
  }

  // Calculate statistics
  const totalFeatures = featuresAccess?.length || 0;
  const enabledPermissions = featuresAccess?.reduce((count, feature) => {
    return count + (feature.view === 'Y' ? 1 : 0) + (feature.create === 'Y' ? 1 : 0) +
      (feature.update === 'Y' ? 1 : 0) + (feature.delete === 'Y' ? 1 : 0);
  }, 0) || 0;
  const totalPermissions = totalFeatures * 4; // 4 permissions per feature
  const permissionPercentage = totalPermissions > 0 ? Math.round((enabledPermissions / totalPermissions) * 100) : 0;

  return (
    <StyledCard>
      <FormikProvider value={formik}>
        <Form autoComplete="off" id="roles" noValidate onSubmit={handleSubmit}>
          {/* Header Section */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={3}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: `linear-gradient(135deg, ${(theme) => theme.palette.primary.main} 0%, ${(theme) => theme.palette.primary.light} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (theme) => theme.customShadows.z4,
                }}
              >
                <Icon icon={lockFill} width={24} height={24} color="white" />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    background: `linear-gradient(135deg, ${(theme) => theme.palette.primary.main} 0%, ${(theme) => theme.palette.secondary.main} 50%, ${(theme) => theme.palette.primary.dark} 100%)`,
                    backgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 700,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {isCreateRole ? 'Create New Role' : isReadOnly ? 'Role Details' : ''}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  {isCreateRole
                    ? 'Define a new role with specific permissions and access controls'
                    : isReadOnly
                      ? 'View role information and assigned permissions'
                      : 'Modify role settings and permission assignments'
                  }
                </Typography>
              </Box>
            </Stack>

            {/* Statistics Cards */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <StatsCard>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, ${(theme) => theme.palette.primary.main} 0%, ${(theme) => theme.palette.primary.light} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: (theme) => theme.customShadows.z4,
                      }}
                    >
                      <Icon icon={settingsFill} width={24} height={24} color="white" />
                    </Box>
                    <Box>
                      <Typography variant="h4" color="primary.main" fontWeight={700}>
                        {totalFeatures}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Features
                      </Typography>
                    </Box>
                  </Stack>
                </StatsCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatsCard>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, ${(theme) => theme.palette.success.main} 0%, ${(theme) => theme.palette.success.light} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: (theme) => theme.customShadows.z4,
                      }}
                    >
                      <Icon icon={checkmarkCircle2Fill} width={24} height={24} color="white" />
                    </Box>
                    <Box>
                      <Typography variant="h4" color="success.main" fontWeight={700}>
                        {enabledPermissions}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Enabled Permissions
                      </Typography>
                    </Box>
                  </Stack>
                </StatsCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <StatsCard>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: `linear-gradient(135deg, ${(theme) => theme.palette.info.main} 0%, ${(theme) => theme.palette.info.light} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: (theme) => theme.customShadows.z4,
                      }}
                    >
                      <Icon icon={closeCircleFill} width={24} height={24} color="white" />
                    </Box>
                    <Box>
                      <Typography variant="h4" color="info.main" fontWeight={700}>
                        {permissionPercentage}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Permission Coverage
                      </Typography>
                    </Box>
                  </Stack>
                </StatsCard>
              </Grid>
            </Grid>
          </Box>

          {/* Form Section */}
          <FormSection>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
              Role Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Role Name"
                  variant="outlined"
                  {...getFieldProps("roleName")}
                  error={Boolean(touched.roleName && errors.roleName)}
                  helperText={touched.roleName && errors.roleName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Chip
                    label={isCreateRole ? 'New Role' : isReadOnly ? 'View Only' : 'Editable'}
                    color={isCreateRole ? 'primary' : isReadOnly ? 'default' : 'secondary'}
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </FormSection>

          {/* Permissions Section */}
          <PermissionsSection>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
              Feature Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure access permissions for each feature. Check the boxes to grant specific permissions.
            </Typography>
            <BaseTable
              rows={featuresAccess ?? []}
              columns={featureColumns(isReadOnly)}
              loading={featureLoading}
              getRowId={(row) => row.featureId}
              pagination={false}
              height={480}
              showExportCsvBtn={false}
              sx={{
                '& .MuiDataGrid-root': {
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: (theme) => theme.palette.primary.lighter,
                    borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontWeight: 600,
                    color: (theme) => theme.palette.primary.main,
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    }
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  }
                }
              }}
            />
          </PermissionsSection>

          {/* Action Section */}
          {!isReadOnly && (
            <ActionSection>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  size="large"
                  type="button"
                  variant="outlined"
                  component={RouterLink}
                  to="/roles"
                  startIcon={<Icon icon="eva:close-outline" />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    },
                  }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  disabled={isReadOnly}
                  loading={isSubmitting || isLoading}
                  startIcon={<Icon icon="eva:save-outline" />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${(theme) => theme.palette.primary.main} 0%, ${(theme) => theme.palette.primary.dark} 100%)`,
                    boxShadow: (theme) => theme.customShadows.z8,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${(theme) => theme.palette.primary.dark} 0%, ${(theme) => theme.palette.primary.main} 100%)`,
                      boxShadow: (theme) => theme.customShadows.z12,
                    },
                  }}
                >
                  {isSubmitting || isLoading ? 'Saving...' : 'Save Changes'}
                </LoadingButton>
              </Stack>
            </ActionSection>
          )}
        </Form>
      </FormikProvider>
    </StyledCard>
  );
};
